import React from "react";
import {Tag} from "primereact/tag";
import {useT} from "./host";

// small shared pieces of «Smart forms», in the design system's Tag / text-xs vocabulary
const FORM_SEVERITY = {draft: "secondary", published: "success", disabled: "warning", archived: "secondary"};
export function StatusTag({status}) {
    const t = useT();
    return <Tag className="text-xs" rounded severity={FORM_SEVERITY[status] || "secondary"} value={t(`forms.status.${status}`)}/>;
}

export const SUBMISSION_STATUSES = ["received", "processing", "completed", "partially_failed", "needs_review", "rejected", "test", "completed_early"];
const SUBMISSION_SEVERITY = {
    received: "info", processing: "info", completed: "success", partially_failed: "danger", needs_review: "warning",
    rejected: "secondary", test: "secondary", completed_early: "success",
};
export function SubmissionTag({status}) {
    const t = useT();
    return <Tag className="text-xs" rounded severity={SUBMISSION_SEVERITY[status] || "secondary"} value={t(`forms.submission-status.${status}`)}/>;
}

const RUN_SEVERITY = {succeeded: "success", skipped: "secondary", failed: "danger", needs_review: "warning", running: "info"};
export function RunTag({status}) {
    const t = useT();
    return <Tag className="text-xs" rounded severity={RUN_SEVERITY[status] || "secondary"} value={t(`forms.run-status.${status}`)}/>;
}

// «n minutes ago» … then a date
export function useRelative(format) {
    const t = useT();
    return (value) => {
        if (!value) return "—";
        const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
        if (minutes < 1) return t("forms.time.now");
        if (minutes < 60) return t("forms.time.minutes", {n: format.number(minutes)});
        const hours = Math.round(minutes / 60);
        if (hours < 24) return t("forms.time.hours", {n: format.number(hours)});
        const days = Math.round(hours / 24);
        if (days < 30) return t("forms.time.days", {n: format.number(days)});
        return format.date(value, false);
    };
}

// a titled card section (surface-card / border-200 / border-round-2xl like the customers pages)
export function Section({title, end, children, className = ""}) {
    return (
        <section className={`surface-card border-1 border-200 border-round-2xl p-3 flex flex-column gap-3 ${className}`.trim()}>
            {(title || end) && (
                <div className="flex align-items-center justify-content-between gap-2">
                    {title && <strong className="text-sm text-800">{title}</strong>}
                    {end}
                </div>
            )}
            {children}
        </section>
    );
}

// label + control, as the app's InputLabel does (text-xs, label above)
export function Field({label, hint, required, children, className = ""}) {
    return (
        <div className={`flex flex-column gap-1 text-xs ${className}`.trim()}>
            {label && <span className="pr-2 text-700">{required && <i className="text-red-500">*</i>}{label}</span>}
            {children}
            {hint && <span className="text-400 line-height-3">{hint}</span>}
        </div>
    );
}

// empty state, as on the customers / events pages
export function EmptyState({icon, title, description, children}) {
    return (
        <div className="flex flex-column align-items-center text-center gap-3 p-5">
            <span className="surface-100 border-circle p-4 flex align-items-center justify-content-center"><i className={`${icon} text-3xl text-primary`}/></span>
            {title && <strong className="text-sm text-800">{title}</strong>}
            {description && <span className="text-xs text-500 line-height-3">{description}</span>}
            {children}
        </div>
    );
}

// Form looks («form look» in the prototype): named presets on the app's palette classes, no inline colors.
// Older drafts may hold calm / dark / warm; they map to the nearest preset.
export const THEMES = {
    zofino: {accent: "border-primary", soft: "surface-ground", tint: "surface-50", severity: undefined, swatch: "bg-primary"},
    yas: {accent: "border-purple-500", soft: "bg-purple-50", tint: "bg-purple-50", severity: "help", swatch: "bg-purple-500"},
    darya: {accent: "border-blue-500", soft: "bg-blue-50", tint: "bg-blue-50", severity: "info", swatch: "bg-blue-500"},
    jangal: {accent: "border-green-500", soft: "bg-green-50", tint: "bg-green-50", severity: "success", swatch: "bg-green-500"},
    kerem: {accent: "border-yellow-500", soft: "bg-yellow-50", tint: "bg-yellow-50", severity: "warning", swatch: "bg-yellow-500"},
};
const LEGACY_THEMES = {calm: "darya", dark: "zofino", warm: "kerem"};
export const themeKey = (meta) => {
    const key = meta?.theme?.preset || "zofino";
    return THEMES[key] ? key : LEGACY_THEMES[key] || "zofino";
};
export const themeOf = (meta) => THEMES[themeKey(meta)];

// «saved in»: where a response landed, from the server's outputs (customer / task / notify / responses)
export function useOutputsText() {
    const t = useT();
    return (outputs) => {
        const list = (outputs || ["responses"]).filter((item) => item !== "notify");
        return (list.length ? list : ["responses"]).map((item) => t(`forms.output.${item}`)).join(t("forms.output.join"));
    };
}

// a stat card, as the prototype's summary cards (label, big value, icon)
export function StatTile({icon, label, value, tone = "text-primary"}) {
    return (
        <div className="surface-card border-1 border-200 border-round-2xl p-3 flex align-items-center gap-3 h-full">
            <span className="surface-100 border-round-xl w-3rem h-3rem flex align-items-center justify-content-center flex-none"><i className={`${icon} text-xl ${tone}`}/></span>
            <div className="flex flex-column gap-1 min-w-0">
                <span className="text-xs text-500">{label}</span>
                <strong className="text-lg text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">{value}</strong>
            </div>
        </div>
    );
}
